import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { ChevronLeft, ChevronRight, Calendar, TrendingUp, Target } from 'lucide-react';

/**
 * Reusable Production Calendar Component
 * Used for both Egg and Milk tracking with consistent UX
 */
const ProductionCalendar = ({ 
  data = {},  // { daily: { "2026-02-01": { total: 10, ... } }, total: 100, average_daily: 5 }
  onDateClick,
  title = "Production",
  unit = "items",
  icon: Icon = Calendar,
  colorTheme = "amber", // amber for eggs, blue for milk
  loading = false
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const colorClasses = {
    amber: {
      bg: "bg-amber-500",
      bgLight: "bg-amber-100 dark:bg-amber-900/30",
      text: "text-amber-600 dark:text-amber-400",
      border: "border-amber-200 dark:border-amber-800"
    },
    blue: {
      bg: "bg-blue-500",
      bgLight: "bg-blue-100 dark:bg-blue-900/30",
      text: "text-blue-600 dark:text-blue-400",
      border: "border-blue-200 dark:border-blue-800"
    },
    green: {
      bg: "bg-green-500",
      bgLight: "bg-green-100 dark:bg-green-900/30",
      text: "text-green-600 dark:text-green-400",
      border: "border-green-200 dark:border-green-800"
    }
  };
  
  const colors = colorClasses[colorTheme] || colorClasses.amber;
  
  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay(); // 0 = Sunday
    
    const days = [];
    
    // Add padding for days before the 1st
    for (let i = 0; i < startPadding; i++) {
      days.push({ date: null, dayNum: null });
    }
    
    // Add actual days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayData = data.daily?.[dateStr];
      days.push({
        date: dateStr,
        dayNum: d,
        total: dayData?.total || 0,
        hasData: !!dayData
      });
    }
    
    return days;
  }, [currentMonth, data]);
  
  // Calculate stats for current month
  const monthStats = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
    
    let total = 0;
    let daysWithData = 0;
    let maxDay = 0;
    
    Object.entries(data.daily || {}).forEach(([date, dayData]) => {
      if (date.startsWith(monthStr)) {
        total += dayData.total || 0;
        daysWithData++;
        maxDay = Math.max(maxDay, dayData.total || 0);
      }
    });
    
    return {
      total,
      daysWithData,
      average: daysWithData > 0 ? (total / daysWithData).toFixed(1) : 0,
      maxDay
    };
  }, [currentMonth, data]);
  
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  const goToToday = () => {
    setCurrentMonth(new Date());
  };
  
  const getIntensityClass = (value, max) => {
    if (!value || value === 0) return "bg-muted/30";
    const ratio = max > 0 ? value / max : 0;
    if (ratio >= 0.8) return `${colors.bg} text-white`;
    if (ratio >= 0.5) return `${colors.bgLight} ${colors.text}`;
    return `${colors.bgLight} opacity-60 ${colors.text}`;
  };
  
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };
  
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  return (
    <Card className="card-farm" data-testid="production-calendar">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Icon className={`w-5 h-5 ${colors.text}`} />
            {title}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={prevMonth} className="h-8 w-8">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={goToToday} className="text-xs">
              Today
            </Button>
            <Button variant="ghost" size="icon" onClick={nextMonth} className="h-8 w-8">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{formatMonthYear(currentMonth)}</p>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Month Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className={`p-2 rounded-lg ${colors.bgLight} text-center`}>
                <p className={`font-heading text-xl font-bold ${colors.text}`}>
                  {monthStats.total.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Total {unit}</p>
              </div>
              <div className="p-2 rounded-lg bg-muted/50 text-center">
                <p className="font-heading text-xl font-bold text-foreground">
                  {monthStats.average}
                </p>
                <p className="text-xs text-muted-foreground">Daily Avg</p>
              </div>
              <div className="p-2 rounded-lg bg-muted/50 text-center">
                <p className="font-heading text-xl font-bold text-foreground">
                  {monthStats.daysWithData}
                </p>
                <p className="text-xs text-muted-foreground">Days Logged</p>
              </div>
            </div>
            
            {/* Week Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {weekDays.map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, idx) => (
                <div
                  key={idx}
                  className={`
                    aspect-square rounded-md flex flex-col items-center justify-center text-xs
                    ${day.date ? 'cursor-pointer hover:ring-2 ring-primary/50 transition-all' : ''}
                    ${day.date ? getIntensityClass(day.total, monthStats.maxDay) : 'bg-transparent'}
                  `}
                  onClick={() => day.date && onDateClick?.(day.date, day.total)}
                  data-testid={day.date ? `cal-day-${day.date}` : undefined}
                >
                  {day.dayNum && (
                    <>
                      <span className="font-medium">{day.dayNum}</span>
                      {day.hasData && (
                        <span className="text-[10px] font-bold">{day.total}</span>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-muted/30"></div>
                <span>No data</span>
              </div>
              <div className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded ${colors.bgLight} opacity-60`}></div>
                <span>Low</span>
              </div>
              <div className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded ${colors.bgLight}`}></div>
                <span>Medium</span>
              </div>
              <div className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded ${colors.bg}`}></div>
                <span>High</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductionCalendar;
